import React, { useEffect, useState, useRef } from 'react';
import {
    AppBar,
    Toolbar,
    Typography,
    Container,
    Box,
    Avatar,
    Button,
    TextField,
    InputAdornment,
    Tabs,
    Tab,
    Stack,
    Chip,
    AvatarGroup,
    Hidden, // For demonstrating conditional rendering if needed
    Grid,
    createTheme,
    ThemeProvider,
    CssBaseline,
    IconButton,
    Paper,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Modal,
    Skeleton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import ShareIcon from '@mui/icons-material/Share';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import ChatList from '../Layout/ChatList';
import FriendsList from '../Layout/FriendList';
import FollowersList from '../Layout/FollowersList';
import SocialFeed from '../SocialFeed';
import GroupJoinedList from '../GroupJoined';
import PostModal from '../Layout/PostModal';
import { get, post } from '../../utility/fetchWeb';
import { get as gets, post as posts, put } from '../../utility/fetch';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import GroupJoinModal from '../Layout/GroupJoinModal';
import notification from '../../utility/notification';
import GroupCreated from '../GroupCreated';
import { format } from 'date-fns';
import { Cancel, CancelRounded, ExitToApp, TimeToLeaveRounded } from '@mui/icons-material';
import Media from '../Layout/Media';
import { useLocation, useSearchParams, Navigate, useNavigate } from 'react-router-dom';
import UploadButton from '../Layout/UploadButton'; // <-- Add this import
import { debounce } from 'lodash'; // Add lodash for debouncing


// Example profile image and cover photo
const profilePic = 'https://i.imgur.com/example-profile-pic.jpg'; // Replace with actual image URL
// Replace with actual image URL
// Fallback if image URLs are not available:
// const profilePic = 'https://via.placeholder.com/150/0000FF/FFFFFF?text=GP';
// const coverPhoto = 'https://via.placeholder.com/1200x300/CCCCCC/FFFFFF?text=Cover+Photo';

const initialProfilePic = 'https://placehold.co/150x150/0000FF/FFFFFF?text=GP';
const initialCoverPhoto = 'https://placehold.co/1200x300/CCCCCC/FFFFFF?text=Cover+Photo';
// Custom MUI theme for consistent styling
const theme = createTheme({
    palette: {
        primary: {
            main: '#28a745', // Green from the design
        },
        secondary: {
            main: '#f8f9fa', // Light background
        },
        text: {
            primary: '#333',
            secondary: '#6c757d',
        },
    },
    typography: {
        fontFamily: 'Roboto, sans-serif',
        h4: {
            fontWeight: 600,
            fontSize: '2rem',
            '@media (max-width:600px)': {
                fontSize: '1.5rem',
            },
        },
        body1: {
            fontSize: '1rem',
            '@media (max-width:600px)': {
                fontSize: '0.9rem',
            },
        },
        caption: {
            fontSize: '0.8rem',
        },
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    borderRadius: '25px',
                    paddingLeft: '18px',
                    paddingRight: '18px',
                },
                containedPrimary: {
                    backgroundColor: '#28a745',
                    '&:hover': {
                        backgroundColor: '#218838',
                    }
                },
                outlined: {
                    borderColor: '#ced4da',
                    color: '#6c757d',
                    '&:hover': {
                        borderColor: '#adb5bd',
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    }
                }
            },
        },
        MuiAvatar: {
            styleOverrides: {
                root: {
                    border: '4px solid white',
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    fontWeight: 600,
                    color: '#6c757d',
                    '&.Mui-selected': {
                        color: '#28a745',
                    },
                },
            },
        },
        MuiTabs: {
            styleOverrides: {
                indicator: {
                    backgroundColor: '#28a745',
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    backgroundColor: '#e0e0e0',
                    color: '#333',
                    borderRadius: '8px',
                    fontWeight: 500,
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    borderRadius: '25px',
                    backgroundColor: '#f5f5f5',
                }
            }
        },
        MuiOutlinedInput: {
            styleOverrides: {
                notchedOutline: {
                    borderColor: '#e0e0e0',
                },
                root: {
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#adb5bd',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#28a745',
                    },
                },
            },
        },
    },
});

function GroupsPage() {
    const location = useLocation();
    const [searchParams] = useSearchParams(); // Get URL query parameters
    const token = sessionStorage.getItem("token"); // Check for token

    // Read selectedGroup and activeTab from navigation state if present
    const initialSelectedGroup = location.state?.selectedGroup || null;
    const notificationID = location.state?.groupId || null;
    const initialActiveTab = typeof location.state?.activeTab === 'number' ? location.state.activeTab : (initialSelectedGroup ? 0 : 3);

    const [activeTab, setActiveTab] = useState(initialActiveTab);
    const [openPostModal, setOpenPostModal] = useState(false);
    const [posts, setPosts] = useState([])
    const [selectedGroup, setSelectedGroup] = useState(initialSelectedGroup);
    const [openJoinGroupModal, setOpenJoinGroupModal] = useState(false);
    const [showAuthModal, setShowAuthModal] = useState(false); // State for auth modal

    // New state for profile and cover photo URLs
    const [profilePicUrl, setProfilePicUrl] = useState(initialProfilePic);
    const [showFeed, setShowFeed] = useState(false);

    // State for remove member confirmation dialog
    const [removeModalOpen, setRemoveModalOpen] = useState(false);
    const [selectedMember, setSelectedMember] = useState(null);

    // State for leave group confirmation dialog
    const [leaveModalOpen, setLeaveModalOpen] = useState(false);

    // New state for search groups
    const [searchGroups, setSearchGroups] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const navigate = useNavigate();

    // Consolidate loading states - remove postsLoading, use single loading state
    const [loading, setLoading] = useState(false); // Single loading state for entire page
    const [highlightedPostId, setHighlightedPostId] = useState(null);

    // Read highlighted post ID from navigation state
    const highlightPostId = location.state?.highlightPostId || null;

    useEffect(() => {
        const encryptedGroupId = searchParams.get('groupId');
        if (encryptedGroupId) {
            const groupId = decryptGroupId(encryptedGroupId);
            fetchGroupById(groupId);
        } else if (notificationID) {
            // Handle direct group ID from notification navigation
            fetchGroupById(notificationID);
        }

        // Handle highlighted post from navigation state
        if (location.state?.highlightPostId) {
            setHighlightedPostId(location.state.highlightPostId);
        }
    }, [searchParams, notificationID, location.state?.highlightPostId]);

    const fetchGroupById = async (groupId) => {
        setLoading(true); // Single loading state
        try {
            // Fetch group data and posts concurrently
            const [groupResponse, postsResponse] = await Promise.all([
                get(`/CommunityGroup/${groupId}`),
                get(`/posts/get-group-posts-including-comments?groupId=${groupId}&pageNumber=1&pageSize=10`)
            ]);

            if (groupResponse?.statusCode === 200) {
                setSelectedGroup(groupResponse?.data);

                // Set posts data
                if (postsResponse?.posts?.length > 0) {
                    setPosts(postsResponse?.posts || []);
                }

                // If there's a highlighted post, switch to conversations tab and set highlighted post
                if (location.state?.highlightPostId) {
                    setHighlightedPostId(location.state.highlightPostId);
                    setActiveTab(2);
                    setShowFeed(true); // Always switch to Conversations tab when there's a highlighted post
                } else {
                    setActiveTab(0); // Set to Group Info tab when fetching from notification without highlighted post
                }
            } else {
                notification({
                    type: 'error',
                    message: 'Failed to fetch group details.',
                });
            }
        } catch (error) {
            console.error('Error fetching group:', error);
            notification({
                type: 'error',
                message: 'Failed to fetch group details.',
            });
        } finally {
            setLoading(false); // Single loading state
        }
    };

    const handleTabChange = (event, newValue) => {
        if (!token && newValue !== 0 && newValue !== 2) {
            setShowAuthModal(true); // Show modal if token is absent and tab is restricted
        } else {
            setActiveTab(newValue);
        }
    };

    const handleOpenPostModal = () => {
        setOpenPostModal(true)
    }

    const handleClosePostModal = () => {
        setOpenPostModal(false);
    }

    const submitPost = async (postData) => {
        console.log('New Post Data:', postData);
        let res = await post(`/posts`, postData); // Using mock post
        if (res) {
            notification({ // Using mock notification
                type: 'success',
                message: 'Post created successfully!',
                duration: 3000,
            })
            getPosts(selectedGroup?.id); // Using mock getPosts
            setOpenPostModal(false);
        }
    }

    const getPosts = async (groupId) => {
        try {
            let res = await get(`/posts/get-group-posts-including-comments?groupId=${groupId}&pageNumber=1&pageSize=10`)
            if (res?.posts?.length > 0) {
                setPosts(res?.posts || []);
            }
            console.log(res)
        } catch (e) {
            console.error('Error fetching posts:', e);
        }
    }

    // Function to handle profile picture change
    const handleProfilePicChange = async (event) => {
        const file = event.target.files[0];
        if (file) {
            console.log('Selected Profile Picture:', file.name);
            const reader = new FileReader();
            reader.onloadend = () => {
                setProfilePicUrl(reader.result);
                notification({ type: 'success', message: 'Profile picture preview updated!', duration: 3000 }); // Using mock notification
            };
            reader.readAsDataURL(file); // Read the file as a data URL for preview
        }
    };

    const isGroupAdmin = selectedGroup &&
        selectedGroup.communityGroupAdmin?.employee?.id === Number(sessionStorage.getItem("userId"));

    const coverPhotoToShow = selectedGroup?.pictureUrl || initialCoverPhoto;

    useEffect(() => {
        // If navigation state changes (e.g., via navigation), update state accordingly
        if (location.state?.selectedGroup) {
            setSelectedGroup(location.state.selectedGroup);
        }
        if (typeof location.state?.activeTab === 'number') {
            setActiveTab(location.state.activeTab);
        }
        // eslint-disable-next-line
    }, [location.state]);



    // Update this useEffect to handle tab switching and fetch posts when group changes
    useEffect(() => {
        if (selectedGroup?.id) {
            // Fetch posts when a new group is selected (not during initial loading)
            if (!loading) {
                getPosts(selectedGroup.id);
            }

            if (showFeed) {
                getPosts(selectedGroup.id);
            } else {
                setActiveTab(0); // Reset to Group Info tab when not showing feed
            }

        } else {
            setActiveTab(3); // Switch to Group Joined tab when no group is selected
            setPosts([]); // Clear posts when no group is selected
        }
    }, [selectedGroup, loading, showFeed]); // Removed highlightedPostId and location.state?.highlightPostId dependencies

    // Function to handle member removal
    const handleRemoveMember = (member) => {
        setSelectedMember(member);
        setRemoveModalOpen(true);
    };

    const handleConfirmRemove = async () => {
        try {
            const response = await put(
                `/communitygroup/${selectedGroup.id}/community-group-member/${selectedMember.id}/remove-community-group-member`
            );

            if (response.statusCode === 200) {
                // Update the group members list locally
                const updatedMembers = selectedGroup.communityGroupMembers.filter(
                    m => m.id !== selectedMember.id
                );

                setSelectedGroup({
                    ...selectedGroup,
                    communityGroupMembers: updatedMembers
                });

                notification({
                    type: 'success',
                    message: 'Member removed successfully',
                    duration: 3000
                });
            }
        } catch (error) {
            console.error('Error removing member:', error);
            notification({
                type: 'error',
                message: 'Failed to remove member',
                duration: 3000
            });
        } finally {
            setRemoveModalOpen(false);
            setSelectedMember(null);
        }
    };

    const handleJoin = async (group) => {
        console.log(`Joining group: ${group.id}`);
        console.log(group);

        let res = await put(`/communitygroup/${group?.id}/join-community-group`);
        if (res?.statusCode === 200) {
            notification({
                type: "success",
                message: res?.data?.message,
            });
            // Update selectedGroup to include the current user as a member
            setSelectedGroup((prevGroup) => ({
                ...prevGroup,
                communityGroupMembers: [
                    ...(prevGroup?.communityGroupMembers || []),
                    { patient: { id: Number(sessionStorage.getItem("userId")) } },
                ],
            }));
        } else {
            notification({
                type: "error",
                message: res?.data?.message || "Something went wrong joining group",
            });
        }
        console.log(res);

        // Add your API or state logic here
    };


    // Function to handle leaving the group
    const handleLeaveGroup = async () => {
        try {
            const currentMember = Number(sessionStorage.getItem("userId"));


            if (!currentMember) {
                throw new Error('Member not found');
            }

            const response = await put(
                `/communitygroup/${selectedGroup.id}/community-group-member/${currentMember}/leave-community-group`
            );

            if (response.statusCode === 200) {
                notification({
                    type: 'success',
                    message: 'Successfully left the group',
                    duration: 3000
                });
                setSelectedGroup(null);
                setActiveTab(0); // Switch back to Group Joined tab
            }
        } catch (error) {
            console.error('Error leaving group:', error);
            notification({
                type: 'error',
                message: 'Failed to leave group',
                duration: 3000
            });
        } finally {
            setLeaveModalOpen(false);
        }
    };

    const fetchSearchGroups = async (filterOn = 'CommunityGroupName', filterQuery = '') => {
        try {
            const endpoint = filterQuery.trim()
                ? `/CommunityGroup/filter-list/${filterOn}/${filterQuery}`
                : '/communitygroup/list';
            const response = await get(endpoint);
            if (response?.statusCode === 200) {
                setSearchGroups(response?.data);
            }
        } catch (error) {
            console.error('Error fetching search groups:', error);
        }
    };


    useEffect(() => {
        fetchSearchGroups()
    }, [])


    // Debounced version of fetchSearchGroups
    const debouncedFetchSearchGroups = useRef(
        debounce((query) => fetchSearchGroups('CommunityGroupName', query), 300)
    ).current;

    // New function to handle viewing group details
    const handleViewGroup = (group) => {
        setSelectedGroup(group);
        setActiveTab(0); // Switch to Group Info tab
    };

    const encryptGroupId = (groupId) => {
        // Example encryption using Base64
        return btoa(groupId.toString());
    };

    const decryptGroupId = (encryptedId) => {
        // Example decryption using Base64
        return atob(encryptedId);
    };

    const handleShareGroup = () => {
        if (selectedGroup?.id) {
            const encryptedId = encryptGroupId(selectedGroup?.id);
            const shareableLink = `${window.location.origin}/groups-page?groupId=${encryptedId}`;
            navigator.clipboard.writeText(shareableLink)
                .then(() => {
                    notification({
                        type: 'success',
                        message: 'Group link copied to clipboard!',
                    });
                })
                .catch(() => {
                    notification({
                        type: 'error',
                        message: 'Failed to copy group link.',
                    });
                });
        }
    };



    const handleRestrictedAction = () => {
        if (!token) {
            setShowAuthModal(true); // Show modal for restricted actions
        }
    };

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Box sx={{ backgroundColor: theme.palette.secondary.main, width: "100%", minHeight: '100vh' }}>
                {/* Cover Photo Area - Only show when not loading */}
                {!loading && selectedGroup && (
                    <Box
                        sx={{
                            width: '100%',
                            height: { xs: 180, sm: 250, md: 300 },
                            backgroundImage: `url("${coverPhotoToShow}")`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            position: 'relative',
                        }}
                    >
                        {/* Only show Change Cover Photo button if user is group admin */}
                        {isGroupAdmin && token && (
                            <UploadButton
                                acceptedFiles="image/*"
                                buttonText="Change Cover Photo"
                                onUploadSuccess={(response) => {
                                    if (response?.code === 1) {
                                        const updatedGroup = {
                                            ...selectedGroup,
                                            pictureUrl: response.doclink,
                                            categoryId: selectedGroup.category?.id || selectedGroup.categoryId,
                                        };

                                        put(`/communitygroup/${selectedGroup.id}`, updatedGroup).then(res => {
                                            if (res?.statusCode === 200) {
                                                setSelectedGroup(prev => ({
                                                    ...prev,
                                                    pictureUrl: response.doclink,
                                                }));
                                                notification({
                                                    type: 'success',
                                                    message: 'Cover photo updated successfully',
                                                });
                                            } else {
                                                notification({
                                                    type: 'error',
                                                    message: res?.error?.message || 'Failed to update group photo',
                                                });
                                            }
                                        }).catch(() => {
                                            notification({
                                                type: 'error',
                                                message: 'Failed to update group photo',
                                            });
                                        });
                                    }
                                }}
                                onUploadError={() => {
                                    notification({ type: 'error', message: 'Cover photo upload failed' });
                                }}
                                buttonProps={{
                                    sx: {
                                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                        '&:hover': {
                                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                        },
                                        position: 'absolute',
                                        bottom: 16,
                                        right: 16,
                                    },
                                }}
                            />
                        )}
                    </Box>
                )}

                {/* Skeleton for cover photo - Only show when loading */}
                {loading && (
                    <Skeleton
                        variant="rectangular"
                        width="100%"
                        height={{ xs: 180, sm: 250, md: 300 }}
                        sx={{ bgcolor: 'grey.300' }}
                    />
                )}

                {/* Main Content Area */}
                <Container maxWidth="xl" sx={{ mt: { xs: 8, sm: 10 }, py: 2 }}>
                    <Grid container spacing={2}>
                        {/* Profile Info and Action Buttons */}
                        <Grid item xs={12} md={7} sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'center', md: 'flex-start' }, textAlign: { xs: 'center', md: 'left' } }}>
                            {loading ? (
                                <>
                                    <Skeleton variant="text" width={300} height={60} />
                                    <Skeleton variant="text" width={200} height={30} />
                                </>
                            ) : (
                                <>
                                    <Typography variant="h4" component="h1" gutterBottom sx={{ mt: { xs: 0, md: 2 } }}>
                                        {selectedGroup?.name || 'Groups'}
                                    </Typography>
                                    <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                                        {/* Public. 560 Friends. 68 posts */}
                                    </Typography>
                                </>
                            )}

                            <Box sx={{ width: "100%", mb: 3, display: 'flex', flexDirection: { sm: 'column', md: 'row' }, alignItems: { xs: 'center', md: 'flex-start' }, justifyContent: { xs: 'center', md: 'flex-end' } }}>
                                {/* Friend Avatars - Only show when not loading */}
                                {!loading && selectedGroup && (
                                    <AvatarGroup spacing={"small"} max={6} sx={{ mb: 3, justifyContent: { xs: 'center', md: 'flex-start' } }}>
                                        <Avatar alt="Remy Sharp" src="https://via.placeholder.com/40" />
                                        <Avatar alt="Travis Howard" src="https://via.placeholder.com/40" />
                                        <Avatar alt="Cindy Baker" src="https://via.placeholder.com/40" />
                                        <Avatar alt="Agnes Walker" src="https://via.placeholder.com/40" />
                                        <Avatar alt="Trevor Henderson" src="https://via.placeholder.com/40" />
                                        <Avatar alt="Another Friend" src="https://via.placeholder.com/40" />
                                        <Avatar alt="Yet Another Friend" src="https://via.placeholder.com/40" />
                                    </AvatarGroup>
                                )}

                                {/* Avatar skeletons - Only show when loading */}
                                {loading && (
                                    <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
                                        {[...Array(6)].map((_, index) => (
                                            <Skeleton key={index} variant="circular" width={40} height={40} />
                                        ))}
                                    </Stack>
                                )}

                                {/* Action Buttons */}
                                {loading ? (
                                    <Stack direction="row" spacing={2}>
                                        <Skeleton variant="rounded" width={120} height={36} />
                                        <Skeleton variant="rounded" width={100} height={36} />
                                        <Skeleton variant="rounded" width={110} height={36} />
                                    </Stack>
                                ) : (
                                    <Stack
                                        direction={{ xs: 'column', sm: 'row' }}
                                        spacing={2}
                                        sx={{
                                            width: '100%',
                                            marginLeft: { xs: 0, sm: 2 },
                                            justifyContent: { xs: 'center', md: 'flex-end' },
                                            flexWrap: 'wrap',
                                            '& > *': { mb: { xs: 1.5, sm: 0 } },
                                            mt: { xs: 2, md: 0 },
                                        }}
                                    >
                                        {selectedGroup && Number(selectedGroup?.id && isGroupAdmin) > 0 && token && (
                                            <Button onClick={handleOpenPostModal} variant="contained" startIcon={<AddIcon />}>
                                                Create a Post
                                            </Button>
                                        )}
                                        {/* <Button variant="outlined" startIcon={<ShareIcon />}>
                                            Share
                                        </Button> */}
                                        {/* Replace "Join a Group" button */}
                                        {!selectedGroup?.communityGroupMembers?.some(
                                            (member) => member.employee?.id === Number(sessionStorage.getItem("userId"))
                                        ) && selectedGroup && token && (
                                                <Button
                                                    onClick={() => handleJoin(selectedGroup)}
                                                    variant="contained"
                                                    startIcon={<GroupAddIcon />}
                                                >
                                                    Join This Group
                                                </Button>
                                            )}
                                        {/* Leave Group button */}
                                        {selectedGroup && token && (
                                            <Button
                                                onClick={() => setLeaveModalOpen(true)}
                                                variant="contained"
                                                color="error"
                                                startIcon={<ExitToApp />}
                                            >
                                                Leave Group
                                            </Button>
                                        )}
                                        {selectedGroup && token && (
                                            <Button
                                                onClick={handleShareGroup}
                                                variant="outlined"
                                                startIcon={<ShareIcon />}
                                            >
                                                Share Group
                                            </Button>
                                        )}
                                        {!token && (
                                            <Button
                                                onClick={handleRestrictedAction}
                                                variant="contained"
                                                color="primary"
                                            >
                                                Join This Group
                                            </Button>
                                        )}
                                    </Stack>
                                )}
                            </Box>
                        </Grid>

                    </Grid>

                    {/* Navigation Tabs */}
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: { xs: 4, md: 6 } }}>
                        {loading ? (
                            <Skeleton variant="rounded" width="100%" height={48} />
                        ) : (
                            <Tabs
                                value={activeTab}
                                onChange={handleTabChange}
                                aria-label="profile navigation tabs"
                                variant="scrollable"
                                scrollButtons="auto"
                                allowScrollButtonsMobile
                                sx={{
                                    '& .MuiTabs-flexContainer': {
                                        justifyContent: { xs: 'flex-start', sm: 'center', md: 'flex-start' },
                                    },
                                }}
                            >
                                <Tab label="Group Info" />
                                <Tab disabled={!token} label="Media" />
                                <Tab label="Conversations" />
                                <Tab disabled={!token} label="Group Joined" />
                                <Tab disabled={!token} label="Group Created" />
                                <Tab disabled={!token} label="Find a Group" />
                            </Tabs>
                        )}
                    </Box>

                    {/* Tab Content - Only show when not loading */}
                    {!loading && (
                        <Box sx={{ mt: 4 }}>
                            {/* Group Info Tab - Index 0 */}
                            {activeTab === 0 && (
                                <Stack spacing={4}>
                                    {loading ? (
                                        // Skeleton for Group Info
                                        <>
                                            <Box>
                                                <Skeleton variant="text" width={200} height={32} />
                                                <Skeleton variant="text" width="100%" height={20} />
                                                <Skeleton variant="text" width="80%" height={20} />
                                            </Box>
                                            <Box>
                                                <Skeleton variant="text" width={150} height={32} />
                                                <Skeleton variant="text" width="60%" height={20} />
                                            </Box>
                                            <Box>
                                                <Skeleton variant="text" width={180} height={32} />
                                                <Stack direction="row" spacing={2} alignItems="center">
                                                    <Skeleton variant="circular" width={40} height={40} />
                                                    <Skeleton variant="text" width={150} height={20} />
                                                </Stack>
                                            </Box>
                                            <Box>
                                                <Skeleton variant="text" width={250} height={32} />
                                                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                                                    {[...Array(5)].map((_, index) => (
                                                        <Skeleton key={index} variant="rounded" width={100} height={32} />
                                                    ))}
                                                </Stack>
                                            </Box>
                                        </>
                                    ) : selectedGroup ? (
                                        // Group Info View
                                        <>
                                            <Box>
                                                <Typography variant="h6" gutterBottom>
                                                    {isGroupAdmin ? "Group Description" : "About Group"}
                                                </Typography>
                                                <Typography variant="body2" color="text.primary">
                                                    {selectedGroup.description}
                                                </Typography>
                                            </Box>

                                            <Box>
                                                <Typography variant="h6" gutterBottom>
                                                    Category
                                                </Typography>
                                                <Typography variant="body2" color="text.primary">
                                                    {selectedGroup.category.name} - {selectedGroup.category.description}
                                                </Typography>
                                            </Box>

                                            <Box>
                                                <Typography variant="h6" gutterBottom>
                                                    Group Admin
                                                </Typography>
                                                <Stack direction="row" spacing={2} alignItems="center">
                                                    <Avatar
                                                        src={selectedGroup.communityGroupAdmin?.employee?.pictureUrl}
                                                        alt={`${selectedGroup.communityGroupAdmin?.employee?.firstName} ${selectedGroup.communityGroupAdmin?.employee?.lastName}`}
                                                    />
                                                    <Typography variant="body2">
                                                        {`${selectedGroup.communityGroupAdmin?.employee?.firstName} ${selectedGroup.communityGroupAdmin?.employee?.lastName}`}
                                                    </Typography>
                                                </Stack>
                                            </Box>

                                            <Box>
                                                <Typography variant="h6" gutterBottom>
                                                    Group Members ({selectedGroup.communityGroupMembers?.length || 0})
                                                </Typography>
                                                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                                                    {selectedGroup.communityGroupMembers?.map((member) => {
                                                        const memberName = member.patient?.firstName ?
                                                            `${member.patient.firstName} ${member.patient.lastName}` :
                                                            `${member.employee?.firstName} ${member.employee?.lastName}`;
                                                        const memberPic = member.patient?.pictureUrl || member.employee?.pictureUrl;

                                                        return (
                                                            <Chip
                                                                key={member.id}
                                                                avatar={<Avatar alt={memberName} src={memberPic} />}
                                                                label={memberName}
                                                                variant="outlined"
                                                                onDelete={isGroupAdmin ? () => handleRemoveMember(member) : undefined}
                                                                deleteIcon={
                                                                    isGroupAdmin ?
                                                                        <CancelRounded
                                                                            sx={{
                                                                                display: 'none',
                                                                                '.MuiChip-root:hover &': {
                                                                                    display: 'block'
                                                                                }
                                                                            }}
                                                                        /> :
                                                                        undefined
                                                                }
                                                                sx={{
                                                                    backgroundColor: 'background.paper',
                                                                    '& .MuiChip-avatar': {
                                                                        width: 24,
                                                                        height: 24,
                                                                    },
                                                                    '& .MuiChip-deleteIcon': {
                                                                        color: 'error.main',
                                                                        '&:hover': {
                                                                            color: 'error.dark'
                                                                        }
                                                                    }
                                                                }}
                                                            />
                                                        );
                                                    })}
                                                </Stack>
                                            </Box>

                                            {isGroupAdmin && (
                                                <Box>
                                                    <Typography variant="h6" gutterBottom>
                                                        Group Statistics
                                                    </Typography>
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={6} md={3}>
                                                            <Paper sx={{ p: 2, textAlign: 'center' }}>
                                                                <Typography variant="h4" color="primary">
                                                                    {selectedGroup.communityGroupMembers?.length}
                                                                </Typography>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Members
                                                                </Typography>
                                                            </Paper>
                                                        </Grid>
                                                        <Grid item xs={6} md={3}>
                                                            <Paper sx={{ p: 2, textAlign: 'center' }}>
                                                                <Typography variant="h4" color="primary">
                                                                    {format(new Date(selectedGroup.createdAt), 'MMM yyyy')}
                                                                </Typography>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Created
                                                                </Typography>
                                                            </Paper>
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            )}
                                        </>
                                    ) : (
                                        // Empty state when no group is selected
                                        <Box sx={{ textAlign: 'center', py: 8 }}>
                                            <Typography variant="h6" color="text.secondary" gutterBottom>
                                                No Group Selected
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Select a group from the "Group Joined" tab to view group information.
                                            </Typography>
                                        </Box>
                                    )}
                                </Stack>
                            )}

                            {/* Media Tab - Index 1 */}
                            {activeTab === 1 && token && (
                                <Media groupId={selectedGroup?.id} />
                            )}

                            {/* Conversations Tab - Index 2 */}
                            {activeTab === 2 && (
                                loading ? (
                                    // Show skeleton only when initially loading
                                    <Stack spacing={3}>
                                        {[...Array(3)].map((_, index) => (
                                            <Box key={index} sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                                                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                                                    <Skeleton variant="circular" width={40} height={40} />
                                                    <Box>
                                                        <Skeleton variant="text" width={120} height={20} />
                                                        <Skeleton variant="text" width={80} height={16} />
                                                    </Box>
                                                </Stack>
                                                <Skeleton variant="text" width="100%" height={20} />
                                                <Skeleton variant="text" width="80%" height={20} />
                                                <Skeleton variant="rectangular" width="100%" height={200} sx={{ mt: 2, borderRadius: 1 }} />
                                            </Box>
                                        ))}
                                    </Stack>
                                ) : (
                                    <SocialFeed
                                        groupId={selectedGroup?.id}
                                        posts={posts}
                                        setPosts={setPosts}
                                        getPosts={getPosts}
                                        selectedGroup={selectedGroup}
                                        setSelectedGroup={setSelectedGroup}
                                        setShowFeed={setShowFeed}
                                        isAdmin={isGroupAdmin}
                                        highlightedPostId={highlightedPostId}
                                        setHighlightedPostId={setHighlightedPostId}
                                    />
                                )
                            )}

                            {/* Group Joined Tab - Index 3 */}
                            {activeTab === 3 && token && (
                                <GroupJoinedList
                                    selectedGroup={selectedGroup}
                                    setSelectedGroup={setSelectedGroup}
                                />
                            )}

                            {/* Groups Created Tab - Index 4 */}
                            {activeTab === 4 && token && (
                                <GroupCreated
                                    selectedGroup={selectedGroup}
                                    setSelectedGroup={setSelectedGroup}
                                />
                            )}

                            {/* Search Groups Tab - Index 5 */}
                            {activeTab === 5 && token && (
                                <Box>
                                    <TextField
                                        variant="outlined"
                                        placeholder="Search Groups"
                                        value={searchQuery}
                                        onChange={(e) => {
                                            setSearchQuery(e.target.value);
                                            debouncedFetchSearchGroups(e.target.value); // Trigger search on input change
                                        }}
                                        sx={{ mb: 2, width: '100%' }}
                                    />
                                    <List>
                                        {searchGroups.map((group) => (
                                            <ListItem key={group.id}>
                                                <ListItemAvatar>
                                                    <Avatar src={group.pictureUrl} alt={group.name} />
                                                </ListItemAvatar>
                                                <ListItemText
                                                    primary={group.name}
                                                    secondary={group.description.substring(0, 25) + '...'}
                                                />
                                                <Button
                                                    variant="contained"
                                                    onClick={() => handleViewGroup(group)}
                                                >
                                                    View Group
                                                </Button>
                                            </ListItem>
                                        ))}
                                    </List>
                                </Box>
                            )}
                        </Box>
                    )}

                    {/* Tab Content Skeletons - Only show when loading */}
                    {loading && (
                        <Box sx={{ mt: 4 }}>
                            <Stack spacing={4}>
                                <Box>
                                    <Skeleton variant="text" width={200} height={32} />
                                    <Skeleton variant="text" width="100%" height={20} />
                                    <Skeleton variant="text" width="80%" height={20} />
                                </Box>
                                <Box>
                                    <Skeleton variant="text" width={150} height={32} />
                                    <Skeleton variant="text" width="60%" height={20} />
                                </Box>
                                <Box>
                                    <Skeleton variant="text" width={180} height={32} />
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <Skeleton variant="circular" width={40} height={40} />
                                        <Skeleton variant="text" width={150} height={20} />
                                    </Stack>
                                </Box>
                                <Box>
                                    <Skeleton variant="text" width={250} height={32} />
                                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                                        {[...Array(5)].map((_, index) => (
                                            <Skeleton key={index} variant="rounded" width={100} height={32} />
                                        ))}
                                    </Stack>
                                </Box>
                            </Stack>
                        </Box>
                    )}
                </Container>

                {/* Modals - Only show when not loading */}
                {!loading && (
                    <>
                        <PostModal id={selectedGroup?.id} open={openPostModal} onSubmit={submitPost} handleClose={handleClosePostModal} />
                        <GroupJoinModal open={openJoinGroupModal} onClose={() => setOpenJoinGroupModal(false)} />
                        {/* Confirmation dialog for removing group member */}
                        <Dialog
                            open={removeModalOpen}
                            onClose={() => setRemoveModalOpen(false)}
                        >
                            <DialogTitle>Remove Member</DialogTitle>
                            <DialogContent>
                                <DialogContentText>
                                    Are you sure you want to remove {selectedMember?.patient?.firstName || selectedMember?.employee?.firstName} from this group?
                                </DialogContentText>
                            </DialogContent>
                            <DialogActions>
                                <Button
                                    onClick={() => setRemoveModalOpen(false)}
                                    color="primary"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleConfirmRemove}
                                    color="error"
                                    variant="contained"
                                >
                                    Remove
                                </Button>
                            </DialogActions>
                        </Dialog>

                        {/* Confirmation dialog for leaving group */}
                        <Dialog
                            open={leaveModalOpen}
                            onClose={() => setLeaveModalOpen(false)}
                        >
                            <DialogTitle>Leave Group</DialogTitle>
                            <DialogContent>
                                <DialogContentText>
                                    Are you sure you want to leave this group?
                                </DialogContentText>
                            </DialogContent>
                            <DialogActions>
                                <Button
                                    onClick={() => setLeaveModalOpen(false)}
                                    color="primary"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleLeaveGroup}
                                    color="error"
                                    variant="contained"
                                >
                                    Leave Group
                                </Button>
                            </DialogActions>
                        </Dialog>

                        {/* Modal for login/signup */}
                        <Modal
                            open={showAuthModal}
                            onClose={() => setShowAuthModal(false)}
                            aria-labelledby="auth-modal-title"
                            aria-describedby="auth-modal-description"
                        >
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    width: 300,
                                    bgcolor: 'background.paper',
                                    boxShadow: 24,
                                    p: 4,
                                    borderRadius: 2,
                                }}
                            >
                                <Typography id="auth-modal-title" variant="h6" component="h2" gutterBottom>
                                    Sign Up or Login
                                </Typography>
                                <Typography id="auth-modal-description" variant="body2" color="text.secondary" gutterBottom>
                                    Please sign up or log in to access this feature.
                                </Typography>
                                <Stack direction="row" spacing={2} justifyContent="center" mt={2}>
                                    <Button variant="contained" color="primary" onClick={() => navigate('/login')}>
                                        Login
                                    </Button>
                                    <Button variant="outlined" color="primary" onClick={() => navigate('/create-account')}>
                                        Sign Up
                                    </Button>
                                </Stack>
                            </Box>
                        </Modal>
                    </>
                )}
            </Box>
        </ThemeProvider>
    );
}

export default GroupsPage;
